import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import FrontImgSrc from '../../../../../../../../../src/assets/images/frontLogo16.svg';
import {
  computeCrmSyncMappingFromCustomFieldMapping,
  CrmToFrontCustomFieldMapping,
  OnUpdateMappedFieldHandler,
} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {MappingHeader} from '../../../../../../../../../src/components/common/mapping/mappingHeader';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../../../../../../../src/components/common/skeleton';
import {isCustomFieldEnumType} from '../../../../../../../../../src/helpers/customFieldHelpers';
import {computeCustomFieldIconName} from '../../../../../../../../../src/helpers/customFieldViewHelpers';
import {CrmFieldModel} from '../../../../../../../../../src/models/api/crmFieldModel';
import {CrmMetadataModel} from '../../../../../../../../../src/models/api/crmMetadataModel';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncMetadataFilterModel,
  CrmSyncSourcesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {CustomFieldModel, CustomFieldsEnum} from '../../../../../../../../../src/models/api/customFieldModel';
import {ResourceMap} from '../../../../../../../../../src/store/appTypes';
import {
  CrmPreviewMapping,
  CrmPreviewParentPanelId,
} from '../../../../../../../../../src/store/panels/contacts/contactsManager/crmPreview/crmPreviewActions';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../../../shared/ui/src/styles/commonStyles';
import {SelectedOption} from '../crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsPageResourceConfig, useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';
import {
  computeFrontAccountDefaultFieldIcon,
  computeFrontCardDefaultFieldIcon,
  renderFrontAccountDefaultFieldName,
  renderFrontCardDefaultFieldName,
} from '../crmSyncsPageConfig/crmSyncsPageConfigHelpers';
import {CrmSyncsSectionWrapper} from '../crmSyncsSectionWrapper';
import {CrmFieldsDropdown} from './crmFieldsDropdown';
import {CrmMapping} from './crmMapping';

/*
 * Constants.
 */

const mappingSectionPadding = 6; // px

/*
 * Props.
 */

export interface CrmSyncsMappingProps {
  source: CrmSyncSourcesEnum;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  isDisabled: boolean;
  isAdmin: boolean;
  customFields: ResourceMap<CustomFieldModel>;
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>;
  isLoading?: boolean;
  crmFields?: ReadonlyArray<CrmFieldModel>;
  crmMetadata?: CrmMetadataModel;
  crmFilter?: CrmSyncMetadataFilterModel;
  panelId: CrmPreviewParentPanelId;
  crmSyncId?: number;
  selectedOption?: SelectedOption;
  onPreviewClick: (mapping: CrmPreviewMapping) => void;
  setMappedFields: HandlerOf<ReadonlyArray<CrmToFrontCustomFieldMapping>>;
}

/*
 * Styles.
 */

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const CrmSyncsMapping: FC<CrmSyncsMappingProps> = (props) => {
  const {
    isDisabled,
    source,
    resourceType,
    mappedFields,
    isLoading,
    crmFields,
    crmMetadata,
    isAdmin,
    customFields,
    selectedOption,
    setMappedFields,
  } = props;
  const config = useCrmPageConfig(source);
  const resourceConfig = config[resourceType];
  if (!resourceConfig) {
    return null;
  }

  const onRemoveMappedField = (crmFieldId: string) => {
    const newMappedFields = mappedFields.filter((field) => field.crmFieldId !== crmFieldId);
    setMappedFields(newMappedFields);
  };

  const onAddMappedField = (mapping: CrmToFrontCustomFieldMapping) => {
    setMappedFields([...mappedFields, mapping]);
  };

  const onUpdateMappedField = (crmFieldId: string, customFieldId?: number, newCrmFieldId?: string) => {
    setMappedFields(
      mappedFields.map((field) => {
        if (field.crmFieldId === crmFieldId) {
          return {crmFieldId: newCrmFieldId ?? crmFieldId, customFieldId};
        }
        return field;
      }),
    );
  };

  const requiredFields = resourceConfig.syncMapping.computeRequiredMapping(selectedOption);

  return (
    <CrmSyncsSectionWrapper
      title={resourceConfig.syncMapping.customTitle ?? renderTitle(resourceType)}
      description={
        resourceConfig.syncMapping.customDescription ?? renderDescription(resourceType, config.displayName)
      }
      button={renderPreviewButton(props, resourceConfig)}
      isDisabled={isDisabled}
      isLoading={isLoading}
      renderLoadingState={renderLoadingState}
      paddingTop={mappingSectionPadding}
    >
      <CrmMapping
        source={source}
        renderHeader={() => renderHeader(config.logoTiny, config.displayName)}
        resourceType={resourceType}
        requiredFields={requiredFields}
        mappedFields={mappedFields}
        onRemoveMappedField={onRemoveMappedField}
        onUpdateMappedField={onUpdateMappedField}
        renderCrmCustomFieldMissingError={() => renderCrmMissingError(config.displayName)}
        renderCrmPlaceholder={renderPlaceholder}
        isDisabled={isDisabled}
        isAdmin={isAdmin}
        computeFrontFieldIcon={(fieldId) => computeFrontFieldIcon(resourceType, fieldId, customFields)}
        computeFrontFieldName={(fieldId) => computeFrontFieldName(resourceType, fieldId, customFields)}
        computeCrmFieldName={(fieldId) => computeCrmFieldName(resourceConfig, fieldId, crmFields)}
        computeCrmFieldIcon={(fieldId) =>
          computeCrmFieldIcon(fieldId, resourceConfig, crmFields, crmMetadata)
        }
        computeCrmFieldType={(fieldId) => computeCrmFieldType(fieldId, crmFields)}
        renderAddFieldDropdown={(popoverProps, crmFieldId) =>
          renderAddFieldDropdown(
            popoverProps,
            mappedFields,
            requiredFields,
            crmFieldId,
            crmFields,
            crmMetadata,
            resourceConfig,
            onAddMappedField,
            onUpdateMappedField,
            renderCrmDropdownTitle(resourceType, config.displayName),
          )
        }
        computeFrontTypesFromCrmCustomField={(customFieldId) =>
          computeFrontTypesFromCrmCustomField(customFieldId, resourceConfig, crmFields, crmMetadata)
        }
      />
    </CrmSyncsSectionWrapper>
  );
};

/*
 * Helpers.
 */

function renderPreviewButton(
  {isAdmin, panelId, crmSyncId, selectedOption, mappedFields, onPreviewClick}: CrmSyncsMappingProps,
  resourceConfig: CrmSyncsPageResourceConfig,
) {
  if (!isAdmin || !panelId || !selectedOption || selectedOption?.count === 0 || !crmSyncId) {
    return null;
  }
  const customFieldMapping = [
    ...resourceConfig.syncMapping.computeRequiredMapping(selectedOption),
    ...mappedFields,
  ];
  const mappingObject = computeCrmSyncMappingFromCustomFieldMapping(customFieldMapping);
  return (
    <Button
      variant={ButtonVariantsEnum.LINK}
      leftIcon="eyeSmall"
      onClick={() => onPreviewClick(mappingObject)}
    >
      <Trans id="crm-syncs-page-mapping-preview-button-text">Preview</Trans>
    </Button>
  );
}

function renderLoadingState() {
  return (
    <Skeleton width="600px" height="212px" borderRadius="8px" scheme={SkeletonColorSchemesEnum.GREY10} />
  );
}

function renderAddFieldDropdown(
  popoverProps: PopoverTransferProps,
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>,
  requiredFields: ReadonlyArray<CrmToFrontCustomFieldMapping>,
  crmFieldId: string | undefined,
  crmFields: ReadonlyArray<CrmFieldModel> | undefined,
  crmMetadata: CrmMetadataModel | undefined,
  resourceConfig: CrmSyncsPageResourceConfig,
  onAddMappedField: HandlerOf<CrmToFrontCustomFieldMapping>,
  onUpdateMappedField: OnUpdateMappedFieldHandler,
  dropdownTitle: ReactNode,
) {
  const alreadySelectedIds = [...requiredFields, ...mappedFields].map((field) => field.crmFieldId);
  const filteredCrmFields = resourceConfig.syncMapping.filterCrmFields
    ? resourceConfig.syncMapping.filterCrmFields(crmFields)
    : crmFields;

  return (
    <CrmFieldsDropdown
      popoverProps={popoverProps}
      selectedFieldId={crmFieldId}
      dropdownTitle={dropdownTitle}
      onSelect={(customField) => {
        if (!crmFieldId) {
          onAddMappedField({crmFieldId: customField.id});
        } else {
          onUpdateMappedField(crmFieldId, undefined, customField.id);
        }
      }}
      alreadySelectedIds={alreadySelectedIds}
      crmFields={filteredCrmFields}
      crmMetadata={crmMetadata}
      computeCrmCustomFieldIcon={(customFieldId, fields, metadata) =>
        computeCrmFieldIcon(customFieldId, resourceConfig, fields, metadata)
      }
    />
  );
}

function renderCrmMissingError(crmDisplayName: string) {
  return (
    <Trans id="crm-syncs-page-mapping-crm-field-missing">Field has been deleted in {crmDisplayName}.</Trans>
  );
}

function renderTitle(resourceType: CrmSyncIntegrationResourceTypesEnum) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return <Trans id="crm-syncs-page-accounts-mapping-title">Map how account fields appear in Front</Trans>;
  }
  return <Trans id="crm-syncs-page-contacts-mapping-title">Map how contact fields appear in Front</Trans>;
}

function renderDescription(resourceType: CrmSyncIntegrationResourceTypesEnum, displayName: string) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return (
      <Trans id="crm-syncs-page-accounts-mapping-description">
        Select which {displayName} fields to sync to Front and map them to a Front account custom field.
      </Trans>
    );
  }
  return (
    <Trans id="crm-syncs-page-contacts-mapping-description">
      Select the {displayName} fields you would like to sync to Front, and map them to a Front contact custom
      field. Contacts will be matched via handles.
    </Trans>
  );
}

function computeCrmFieldName(
  resourceConfig: CrmSyncsPageResourceConfig,
  customFieldId: string,
  crmFields?: ReadonlyArray<CrmFieldModel>,
): string | undefined {
  const name = resourceConfig.syncMapping.computeCrmDefaultFieldPlaceholderName(customFieldId);
  if (name || !crmFields || crmFields.length === 0) {
    return name;
  }
  const crmField = crmFields.find((field) => field.id === customFieldId);
  return crmField?.label;
}

function computeCrmFieldIcon(
  customFieldId: string,
  resourceConfig: CrmSyncsPageResourceConfig,
  crmFields?: ReadonlyArray<CrmFieldModel>,
  crmMetadata?: CrmMetadataModel,
): IconName | undefined {
  if (!crmFields || !crmMetadata || crmFields.length === 0) {
    return resourceConfig.syncMapping.computeCrmDefaultFieldPlaceholderIcon(customFieldId);
  }

  const crmField = crmFields.find((field) => field.id === customFieldId);
  if (!crmField) {
    return undefined;
  }

  // if the field has a pre-specified icon use that instead
  const customIconName = resourceConfig.syncMapping.computeCrmDefaultFieldIcon(crmField);
  if (customIconName) {
    return customIconName;
  }

  const allowedTypes = crmMetadata.mappingOptions[crmField.type];

  if (!allowedTypes) {
    return undefined;
  }

  // If we have more than 1 mapping type, try to find a non-string field to use for the icon.
  const validMappingIconTypes = allowedTypes.filter(isCustomFieldEnumType);
  if (validMappingIconTypes.length > 1) {
    const nonStringField = validMappingIconTypes.find(
      (customFieldType) => customFieldType !== CustomFieldsEnum.STRING,
    );
    if (nonStringField) {
      return computeCustomFieldIconName(nonStringField, VisualSizesEnum.SMALL);
    }
  }
  return computeCustomFieldIconName(validMappingIconTypes[0], VisualSizesEnum.SMALL);
}

function computeFrontFieldName(
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  customFieldId: number,
  customFields: ResourceMap<CustomFieldModel>,
) {
  if (customFieldId >= 0) {
    const customField = customFields[customFieldId];
    if (!customField) {
      return undefined;
    }
    return customField.name;
  }

  return resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT
    ? renderFrontAccountDefaultFieldName(customFieldId)
    : renderFrontCardDefaultFieldName(customFieldId);
}

function computeFrontFieldIcon(
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  customFieldId: number,
  customFields: ResourceMap<CustomFieldModel>,
): IconName | undefined {
  if (customFieldId >= 0) {
    const customField = customFields[customFieldId];
    if (!customField) {
      return undefined;
    }
    return computeCustomFieldIconName(customField.type, VisualSizesEnum.SMALL);
  }
  return resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT
    ? computeFrontAccountDefaultFieldIcon(customFieldId)
    : computeFrontCardDefaultFieldIcon(customFieldId);
}

function computeFrontTypesFromCrmCustomField(
  customFieldId: string,
  resourceConfig: CrmSyncsPageResourceConfig,
  crmFields?: ReadonlyArray<CrmFieldModel>,
  crmMetadata?: CrmMetadataModel,
): ReadonlyArray<string> {
  if (!crmMetadata || !crmFields) {
    return [];
  }
  const crmField = crmFields.find((field) => field.id === customFieldId);
  const mappings =
    crmMetadata.mappingOptions[crmField?.type ?? resourceConfig.syncMapping.defaultCrmFieldType];
  return mappings || [];
}

function computeCrmFieldType(
  customFieldId: string,
  crmFields?: ReadonlyArray<CrmFieldModel>,
): string | undefined {
  if (!crmFields) {
    return undefined;
  }
  const crmField = crmFields.find((field) => field.id === customFieldId);
  return crmField?.type ?? undefined;
}

function renderPlaceholder() {
  return (
    <StyledPlaceholderDiv>
      <Trans id="crm-syncs-page-mapping-placeholder">Select field to map from</Trans>
    </StyledPlaceholderDiv>
  );
}

function renderHeader(logo: string, crmDisplayName: string) {
  return (
    <MappingHeader
      sourceImg={logo}
      sourceTitle={<Trans id="crm-syncs-page-mapping-header-crm-title">{crmDisplayName} fields</Trans>}
      destinationImg={FrontImgSrc}
      destinationTitle={
        <Trans id="accounts-crm-dialog-sync-mapping-salesforce-front-fields">Front fields</Trans>
      }
    />
  );
}

function renderCrmDropdownTitle(resourceType: CrmSyncIntegrationResourceTypesEnum, crmDisplayName: string) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return <Trans id="crm-syncs-page-crm-account-dropdown-title">{crmDisplayName} account fields</Trans>;
  }
  return <Trans id="crm-syncs-page-crm-contact-dropdown-title">{crmDisplayName} contact fields</Trans>;
}
